import * as React from 'react';
import { useSelector } from 'react-redux';

import { TableLayout } from '../../components/TableLayout';
import { Table, TableBody } from '../../components/Table';
import { selectProps } from '../../selectors/props';
import { TableGroupContainer } from './parts/Group';
import { PrevButton } from '../../components/PrevButton';
import { NextButton } from '../../components/NextButton';
import { HeaderContainer } from '../Header';
import { useLast } from './helpers';
import { trackTableScroll } from './tracking';
import { selectOffersCount } from '../../selectors/offers';

export const TableContainer: React.VFC = () => {
  const [position, setPosition] = React.useState(0);
  const [sticky, setSticky] = React.useState(false);
  const [wasScrolled, setScrolled] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);

  const props = useSelector(selectProps);
  const offersCount = useSelector(selectOffersCount);

  const contactsRowRef = React.useRef<HTMLTableRowElement>(null);
  const [lastPosition, headRowRef] = useLast(offersCount);
  const isFirst = position === 0;
  const isLast = position >= lastPosition;

  React.useEffect(() => {
    if (position > lastPosition) {
      setPosition(lastPosition);
    }
  }, [lastPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableScroll = React.useCallback(() => {
    if (!wasScrolled) {
      trackTableScroll();
      setScrolled(true);
    }
  }, [wasScrolled]);

  const handleButtonClick = React.useCallback(
    (direction: -1 | 1) => () => {
      setPosition(prev => prev + direction);
      handleTableScroll();
    },
    [handleTableScroll],
  );

  const handleDragEnd = React.useCallback(() => {
    setPosition(prev => {
      const next = Math.round(prev);

      return next >= lastPosition ? lastPosition : next;
    });
  }, [lastPosition]);

  return (
    <TableLayout
      table={
        <>
          <HeaderContainer
            headRowRef={headRowRef}
            stickyRowRef={contactsRowRef}
            position={position}
            onSticky={setSticky}
            prev={isDragging ? null : <PrevButton first={isFirst} onClick={handleButtonClick(-1)} />}
            afterDragEnd={handleDragEnd}
            onDragDrop={setIsDragging}
            onUpdatePosition={setPosition}
          />
          <Table>
            <TableBody>
              {props.map(prop => (
                <TableGroupContainer
                  contactsRowRef={contactsRowRef}
                  key={prop.name}
                  position={position}
                  dragging={isDragging}
                  {...prop}
                />
              ))}
            </TableBody>
          </Table>
        </>
      }
      nextButton={!isDragging && <NextButton sticky={sticky} last={isLast} onClick={handleButtonClick(1)} />}
    />
  );
};
